import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, AdminSelect, AdminTextarea, ConfirmDialog, EmptyState, FieldLabel, Modal, StatusBadge } from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const EMPTY = { title: "", subtitle: "", description: "", icon: "", image: "", cta_label: "", cta_link: "", group: "residential", status: "published", sort_order: 0 };
const GROUPS = [
  { value: "residential", label: "Residential (service pathway)" },
  { value: "bim", label: "BIM Production" },
  { value: "project_management", label: "Project Management" },
];

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");

  const load = () => adminApi.listServices().then(setItems);
  useEffect(() => { load(); }, []);

  const filtered = groupFilter ? items.filter((s) => s.group === groupFilter) : items;

  const openCreate = () => { setCurrent(EMPTY); setOpenForm(true); };
  const openEdit = (s) => { setCurrent(s); setOpenForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...current, sort_order: Number(current.sort_order) || 0 };
      if (current.id) await adminApi.updateService(current.id, payload);
      else await adminApi.createService(payload);
      toast.success("Saved"); setOpenForm(false); load();
    } catch { toast.error("Save failed"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await adminApi.deleteService(confirmDelete.id); toast.success("Deleted"); load(); setConfirmDelete(null); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="body-text">Service cards shown across the site.</p>
        <div className="flex items-center gap-3">
          <AdminSelect value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="w-56">
            <option value="">All groups</option>
            {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </AdminSelect>
          <button type="button" onClick={openCreate} className="btn-primary"><Plus className="h-4 w-4" /> New Service</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No services in this group." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} className="admin-card rounded-sm p-5">
              <div className="flex items-center justify-between">
                <p className="eyebrow-muted uppercase">{s.group.replace("_", " ")}</p>
                <StatusBadge status={s.status} />
              </div>
              <h3 className="text-lg font-semibold mt-3">{s.title}</h3>
              {s.subtitle && <p className="text-sm text-brand-gray mt-1">{s.subtitle}</p>}
              {s.description && <p className="body-text text-sm mt-3 line-clamp-3">{s.description}</p>}
              <div className="mt-4 flex items-center justify-between">
                <button type="button" onClick={() => openEdit(s)} className="btn-outline text-[10px] px-3 py-2">Edit</button>
                <button type="button" onClick={() => setConfirmDelete(s)} className="text-brand-gray hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={openForm} onClose={() => setOpenForm(false)} title={current.id ? "Edit service" : "New service"} wide>
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <label><FieldLabel>Title</FieldLabel><AdminInput required value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} /></label>
            <label><FieldLabel>Subtitle</FieldLabel><AdminInput value={current.subtitle || ""} onChange={(e) => setCurrent({ ...current, subtitle: e.target.value })} /></label>
          </div>
          <label><FieldLabel>Description</FieldLabel><AdminTextarea rows={4} value={current.description || ""} onChange={(e) => setCurrent({ ...current, description: e.target.value })} /></label>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Image</FieldLabel>
              <ImageUploadField
                value={current.image || ""}
                onChange={(value) => setCurrent({ ...current, image: value })}
                category={current.group === "bim" ? "bim" : "residential"}
              />
            </div>
            <label><FieldLabel>Icon (lucide name)</FieldLabel><AdminInput value={current.icon || ""} onChange={(e) => setCurrent({ ...current, icon: e.target.value })} /></label>
            <label><FieldLabel>CTA Label</FieldLabel><AdminInput value={current.cta_label || ""} onChange={(e) => setCurrent({ ...current, cta_label: e.target.value })} /></label>
            <label><FieldLabel>CTA Link</FieldLabel><AdminInput value={current.cta_link || ""} onChange={(e) => setCurrent({ ...current, cta_link: e.target.value })} /></label>
            <label><FieldLabel>Group</FieldLabel>
              <AdminSelect value={current.group} onChange={(e) => setCurrent({ ...current, group: e.target.value })}>
                {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </AdminSelect>
            </label>
            <label><FieldLabel>Sort order</FieldLabel><AdminInput type="number" value={current.sort_order || 0} onChange={(e) => setCurrent({ ...current, sort_order: e.target.value })} /></label>
            <label><FieldLabel>Status</FieldLabel>
              <AdminSelect value={current.status} onChange={(e) => setCurrent({ ...current, status: e.target.value })}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </AdminSelect>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-outline" onClick={() => setOpenForm(false)}>Cancel</button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={doDelete} title="Delete service" description={`Delete '${confirmDelete?.title}'?`} />
    </div>
  );
}
